import {
  Button,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import "./style.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Paper } from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useFormik } from "formik";
import AxiosInstance from "../../utils/axiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import InputText from "../InputFields/InputText";
import * as Yup from "yup";
import showToast from "../Toast/Toster";
import { WhiteLoaderComponent, OrangeButton } from "../Icon/Index";
import PasswordValidation from "../Password/PasswordValidation";
import { useUserType } from "../../contexts/UserTypeContext";
const NewPassword = () => {
  const location = useLocation();
  const [showCPassword, setShowCPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [tokenExpired, setTokenExpired] = useState(false);
  const [loader, setLoader] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { userType, logo } = useUserType();
  const navigate = useNavigate();

  const [randomPassName] = useState(
    `pass_${Math.random().toString(36).slice(2)}`
  );
  const [randomConfirmPassName] = useState(
    `pass_${Math.random().toString(36).slice(2)}`
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get("token");

    AxiosInstance.get(`/v1/forget-pass/check_forget_token_status/${token}`)
      .then((response) => {
        const data = response?.data;
        if (data?.expired) {
          setTokenExpired(true);
        } else {
          setEmail(token);
        }
      })
      .catch((error) => {
        console.error("Error checking token status:", error);
      });
  }, [location.search]);

  const formik = useFormik({
    initialValues: {
      [randomPassName]: "",
      [randomConfirmPassName]: "",
    },
    validationSchema: Yup.object({
      [randomPassName]: PasswordValidation,
      [randomConfirmPassName]: Yup.string()
        .oneOf([Yup.ref(randomPassName), null], "Passwords must match")
        .required("Confirm password is required"),
    }),
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        const response = await AxiosInstance.put(
          `/v1/forget-pass/reset_forget_passwords/${email}`,
          { Password: values[randomPassName] },
          { headers: { "Content-Type": "application/json" } }
        );
        if (response?.status === 200) {
          showToast.success("Password set successfully", {
            position: "top-center",
            autoClose: 1000,
          });
          setTimeout(() => {
            navigate(response?.data?.url);
          }, 1000);
        } else {
          showToast.error("Failed to change password", {
            position: "top-center",
            autoClose: 1000,
          });
        }
      } catch (error) {
        if (error?.response?.status === 401) {
          showToast.warning(error?.response?.data?.message);
        } else if (error?.response?.status === 400) {
          showToast.warning(error?.response?.data?.message);
        } else {
          showToast.error("An error occurred while changing the password");
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (loader) {
    return (
      <WhiteLoaderComponent
        height="20"
        width="20"
        padding="20"
        loader={loader}
      />
    );
  }

  // Define theme colors based on userType
  const isDonor = userType === "donor";
  const themeColors = {
    primary: isDonor ? "#68b2cb" : "var(--amber)",
    primaryLight: isDonor ? "#8ec9db" : "#f4a261",
    background: isDonor ? "linear-gradient(135deg, #E9F8FC 0%, #d4eef5 100%)" : "linear-gradient(135deg, #f8fafc 0%, #e0e7ef 100%)",
    buttonBackground: isDonor 
      ? "linear-gradient(135deg, #68b2cb 0%, #8ec9db 100%)" 
      : "linear-gradient(135deg, var(--amber) 0%, #f4a261 100%)",
    buttonHoverBackground: isDonor 
      ? "linear-gradient(135deg, #8ec9db 0%, #68b2cb 100%)" 
      : "linear-gradient(135deg, #f4a261 0%, var(--amber) 100%)",
    boxShadow: isDonor 
      ? "0 2px 8px rgba(104,178,203,0.15)" 
      : "0 2px 8px rgba(212, 148, 58,0.15)",
  };

  if (tokenExpired) {
    return (
      <Grid
        className="login"
        container
        justifyContent="center"
        alignItems="center"
        sx={{
          minHeight: "100vh",
          background: themeColors.background,
        }}
      >
        <Grid
          size={{
            xs: 11,
            sm: 8,
            md: 5,
            lg: 4
          }}>
          <Paper
            elevation={4}
            sx={{
              padding: 4,
              borderRadius: 4,
              textAlign: "center",
              background: "#fff",
              boxShadow: "var(--shadow-xl)",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 60, color: themeColors.primary, mb: 2 }} />
            <Typography
              variant="h5"
              fontWeight={700}
              color="#0f2b46"
              gutterBottom
            >
              Token Expired
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              Your password reset link has expired.
              <br />
              Please request a new reset link to continue.
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                px: 4,
                background: themeColors.buttonBackground,
                color: "#fff",
                boxShadow: themeColors.boxShadow,
                "&:hover": {
                  background: themeColors.buttonHoverBackground,
                },
              }}
              onClick={() => navigate("/auth/forgot-password")}
            >
              Request New Link
            </Button>
            <Typography
              variant="body2"
              sx={{
                mt: 3,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ChevronLeftIcon
                sx={{ fontSize: 20, color: themeColors.primary }}
              />
              <span
                onClick={() => navigate("/auth/login")}
                style={{ marginLeft: "4px" }}
              >
                Go back to{" "}
                <span style={{ color: themeColors.primary, fontWeight: 600 }}>
                  Login
                </span>
              </span>
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid className="login">
      <Grid container spacing={0} className="vh-100" style={{ margin: 0, padding: 0 }}>
        <Grid
          className="d-flex justify-content-center align-items-center pt-0 forgatePasswordNoneHe forgotTopGive"
          sx={{
            overflowY: "auto",
            overflowX: "hidden",
            maxHeight: { xs: "100vh", md: "100vh" },
            padding: { xs: "20px 0", md: "0" },
          }}
          size={{
            xs: 12,
            md: 6
          }}>
          <Grid style={{width: "100%",display: "flex",flexDirection: "column",alignItems: "center", padding: "0 15px"}}>
          <Typography
            className="app-logo forgotpassWordImageSet"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* JOB-3155: inherit `.app-logo` sizing (~65px) from Login screen instead of the oversized 500px inline width. */}
            <img
              src={logo}
              alt="logo"
              style={{ marginBottom: "20px" }}
            />
          </Typography>
          <Grid
            style={{
              width: "80%",
              maxWidth: "100%",
            }}
            className="d-flex flex-direction-column align-items-center p-2 maon-forget-password"
            sx={{
              width: { xs: "100%", md: "80%" },
              padding: { xs: "10px", md: "16px" },
            }}
          >
            <form
              onSubmit={formik?.handleSubmit}
              className="d-flex flex-column justify-content-between forgoFormSubmitClass new-password-form"
              style={{ 
                width: "100%",
              }}
              autoComplete="off"
              spellCheck="false"
            >
              <div style={{ display: "none" }}>
                <input type="text" id="username" name="username" />
                <input type="password" id="password" name="password" />
              </div>
              <input
                type="text"
                style={{ display: "none" }}
                readOnly={true}
                autoComplete="username"
              />
              <Typography
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  textAlign: "left",
                  marginTop: "13px",
                  display: "flex",
                  cursor: "pointer",
                  alignItems: "center",
                }}
                onClick={() => {
                  navigate("/auth/login");
                }}
              >
                <ChevronLeftIcon className={userType === "donor" ? "text-donor-color" : "text-orange-color"} style={{ fontSize: "30px" }} />
                <span className="mx-2" style={{ fontSize: "16px" }}>Go back to <span className={userType === "donor" ? "text-donor-color" : "text-orange-color"}>Log In</span></span>
              </Typography>

              <Grid>
                <Typography
                  className="text forgotHeadText text-blue-color"
                  style={{ fontSize: "35px", marginTop: "20px" ,marginBottom: "20px"}}
                >
                  Set your password
                </Typography>
                <FormGroup style={{ width: "100%", marginTop: "20px" }}>
                  <InputText
                    value={formik?.values[randomPassName]}
                    onChange={formik?.handleChange}
                    onBlur={formik?.handleBlur}
                    error={
                      formik?.touched[randomPassName] &&
                      Boolean(formik?.errors[randomPassName])
                    }
                    helperText={
                      formik?.touched[randomPassName] &&
                      formik?.errors[randomPassName]
                    }
                    name={randomPassName}
                    label="New Password"
                    required
                    type={showPassword ? "text" : "password"}
                    className="text-blue-color w-100"
                    autoComplete="new-password"
                    inputProps={{
                      autoComplete: "new-password",
                      "data-lpignore": "true",
                    }}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormGroup>
                <FormGroup style={{ width: "100%", marginTop: "20px" }}>
                  <InputText
                    value={formik?.values[randomConfirmPassName]}
                    onChange={formik?.handleChange}
                    onBlur={formik?.handleBlur}
                    error={
                      formik?.touched[randomConfirmPassName] &&
                      Boolean(formik?.errors[randomConfirmPassName])
                    }
                    helperText={
                      formik?.touched[randomConfirmPassName] &&
                      formik?.errors[randomConfirmPassName]
                    }
                    name={randomConfirmPassName}
                    label="Re-type New Password"
                    required
                    type={showCPassword ? "text" : "password"}
                    className="text-blue-color w-100 mb-3"
                    autoComplete="new-password"
                    inputProps={{
                      autoComplete: "new-password",
                      "data-lpignore": "true",
                    }}
                    onPaste={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowCPassword(!showCPassword)}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showCPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormGroup>

                <FormGroup style={{ width: "100%"  }}>
                  <OrangeButton
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      borderRadius: "4px",
                      height: "48px",
                      textTransform: "none",
                    }}
                    type="submit"
                    disabled={submitting}
                    isDonationMode={userType === "donor"}
                    label={submitting ? "Submitting..." : "Submit"}
                  />
                </FormGroup>
              </Grid>
            </form>
          </Grid>
          </Grid>
        </Grid>
        <Grid
          className="d-flex justify-content-center align-items-center img decreaseSizeHei"
          style={{ height: "100vh", padding: 0, margin: 0 }}
          sx={{
            display: { xs: "none", md: "flex" },
          }}
          size={{
            xs: 12,
            md: 6
          }}>
          <div
            className="setImg_cdn"
            style={{ height: "100vh", width: "100%" }}
          ></div>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default NewPassword;
